import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle, Wrench, Shield, Clock, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Header } from './Header';
import { Footer } from './Footer';
import { WhatsAppButton } from './WhatsAppButton';
import { CallButton } from './CallButton';
import { TopAnnouncementBar } from './TopAnnouncementBar';

export const MercedesRepair = () => {
  const mercedesServices = [
    {
      title: "Engine Diagnostics & Repair",
      description: "Advanced Mercedes engine diagnostics using specialized STAR diagnostic tools",
      icon: Wrench
    },
    {
      title: "Transmission Service",
      description: "Expert Mercedes automatic transmission repair and 7G-TRONIC service",
      icon: Wrench
    },
    {
      title: "Brake System Service",
      description: "Complete Mercedes brake inspection, pad replacement, and rotor servicing",
      icon: Shield
    },
    {
      title: "Electrical Systems",
      description: "Mercedes electrical diagnostics and repair for all electronic components",
      icon: Wrench
    },
    {
      title: "Suspension & Steering",
      description: "Mercedes AIRMATIC suspension repair and wheel alignment",
      icon: Wrench
    },
    {
      title: "Oil Change & Maintenance",
      description: "Mercedes-specific oil changes and routine maintenance services",
      icon: Clock
    },
    {
      title: "AC System Repair",
      description: "Mercedes air conditioning service and refrigerant recharge",
      icon: Wrench
    },
    {
      title: "Battery & Alternator",
      description: "Mercedes battery replacement and charging system diagnostics",
      icon: Wrench
    }
  ];

  const mercedesModels = [
    "Mercedes A-Class", "Mercedes B-Class", "Mercedes C-Class", "Mercedes E-Class",
    "Mercedes S-Class", "Mercedes CLA", "Mercedes CLS", "Mercedes GLA",
    "Mercedes GLB", "Mercedes GLC", "Mercedes GLE", "Mercedes GLS",
    "Mercedes G-Class (G-Wagon)", "Mercedes AMG GT", "Mercedes SL", "Mercedes SLC",
    "Mercedes V-Class", "Mercedes AMG Models (C63, E63, S63, GLE63)", "Mercedes EQC", "Mercedes Maybach"
  ];

  const whyChooseUs = [
    {
      title: "Mercedes-Certified Technicians",
      description: "Our mechanics are specially trained and certified for Mercedes repairs"
    },
    {
      title: "Genuine Parts Available",
      description: "We use OEM and genuine Mercedes-Benz parts for all repairs"
    },
    {
      title: "STAR Diagnostic Tools",
      description: "State-of-the-art Mercedes STAR diagnostic equipment"
    },
    {
      title: "Warranty on Services",
      description: "All Mercedes repairs come with comprehensive warranty coverage"
    }
  ];

  const openWhatsApp = () => {
    const message = "Hi Think Mechanic, I need Mercedes repair service and want to claim my 15% website discount!";
    const whatsappNumber = '+971566671468';
    const whatsappUrl = `https://wa.me/${whatsappNumber.replace(/\s/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Mercedes Repair Dubai | German Car Specialists | Think Mechanic</title>
        <meta name="description" content="Expert Mercedes-Benz repair and maintenance in Dubai. Specialized diagnostics, suspension, AC, transmission and engine repair by German car experts." />
        <link rel="canonical" href="https://thinkmechanicdxb.com/mercedes-repair-dubai" />
        <meta name="keywords" content="Mercedes repair Dubai, Mercedes service Dubai, Mercedes garage Dubai, Mercedes workshop Dubai, Mercedes mechanic Dubai, Mercedes specialist Dubai, Mercedes Benz garage Dubai, Mercedes workshop Ras Al Khor, Mercedes garage Ras Al Khor, Mercedes garage near me, Mercedes mechanic near me, Mercedes AC repair Dubai, Mercedes oil change Dubai, Mercedes brake repair Dubai, Mercedes engine repair Dubai, Mercedes transmission repair Dubai, Mercedes maintenance Dubai, Mercedes diagnostic Dubai, Mercedes repair near me, Best Mercedes garage Dubai, Mercedes service center Dubai, Affordable Mercedes repair Dubai, Mercedes oil change near me, Mercedes brake service Dubai, Mercedes suspension repair Dubai, Mercedes AIRMATIC repair, Mercedes electrical repair Dubai, Mercedes battery replacement Dubai, Mercedes tire service Dubai, Think Mechanic Mercedes, Mercedes C class repair, Mercedes E class repair, Mercedes GLE repair Dubai, Mercedes GLC service Dubai, Mercedes repair Ras Al Khor, Emergency Mercedes repair Dubai, Same day Mercedes service, Mercedes COMAND repair, Mercedes check engine light Dubai, Certified Mercedes technician Dubai, Independent Mercedes garage Dubai, Trusted Mercedes mechanic Dubai, Mercedes AMG service Dubai, Mercedes G-Wagon repair, Mercedes C200 service Dubai, Mercedes E200 repair Dubai, Out of warranty Mercedes repair, Mercedes extended warranty Dubai, Mercedes pre-purchase inspection, Used Mercedes inspection Dubai, Top Mercedes garage Dubai, Mercedes service Al Quoz, Mercedes repair Sharjah, car AC repair Dubai, car AC gas refill Dubai, auto AC repair near me" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Mercedes Repair Dubai | Expert Mercedes Service | Think Mechanic" />
        <meta property="og:description" content="Professional Mercedes-Benz repair in Dubai. Certified technicians, genuine parts, 15+ years experience. All Mercedes models serviced." />
        <meta property="og:url" content="https://thinkmechanicdxb.com/mercedes-repair-dubai" />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Mercedes Repair Dubai | Think Mechanic" />
        <meta name="twitter:description" content="Expert Mercedes-Benz repair and maintenance in Dubai. Certified technicians, genuine parts." />
      </Helmet>

      {/* Top Announcement Bar */}
      <TopAnnouncementBar />

      {/* Same Header as Homepage */}
      <Header />

      {/* Hero Section with Mercedes Image Background */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Mercedes Background Image - Mercedes car in professional workshop/garage */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1623516747694-00b70bd7ec49?w=1920&q=85"
            alt="Mercedes-Benz Car in Professional Workshop - Think Mechanic Dubai"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center center' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center py-32">
            {/* Mercedes Logo Badge */}
            <div className="inline-flex items-center justify-center space-x-3 bg-white/10 backdrop-blur-md border-2 border-white/20 px-6 py-3 rounded-full mb-6">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center p-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_think-mechanic/artifacts/j5clohhg_image.png"
                  alt="Mercedes-Benz Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex flex-col items-start">
                <span className="text-white font-bold text-lg">Mercedes-Benz</span>
                <span className="text-white/80 text-xs font-semibold">Repair Specialists</span>
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Expert Mercedes Repair in
              <span className="text-red-600"> Dubai</span>
            </h1>

            {/* Subheading */}
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Specialized Mercedes-Benz repair and maintenance in Ras Al Khor. Trust certified technicians with your luxury vehicle.
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={openWhatsApp}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 rounded-lg text-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-red-600/50 group"
              >
                Book Mercedes Service & Get 15% OFF
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <p className="text-white/90 text-sm">✨ Special discount for website visitors</p>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-6 mt-16 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 mb-2">15+</div>
                <div className="text-gray-300 text-sm">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 mb-2">600+</div>
                <div className="text-gray-300 text-sm">Mercedes Serviced</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 mb-2">100%</div>
                <div className="text-gray-300 text-sm">Satisfaction</div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Element */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent z-10"></div>
      </section>

      {/* Mercedes Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Our Services</span>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mt-3 mb-4">
              Comprehensive Mercedes Repair Services
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              From routine maintenance to complex repairs, we handle all Mercedes-Benz service needs with expertise.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mercedesServices.map((service, index) => (
              <Card key={index} className="bg-white border-gray-200 p-6 hover:border-red-600 transition-all duration-300">
                <div className="bg-red-600/20 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-gray-900 font-bold text-lg mb-2">{service.title}</h3>
                <p className="text-gray-600 text-sm">{service.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Think Mechanic for Mercedes Repair?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {whyChooseUs.map((reason, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="bg-red-600 rounded-full p-2 flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-gray-900" />
                </div>
                <div>
                  <h3 className="text-gray-900 font-bold text-xl mb-2">{reason.title}</h3>
                  <p className="text-gray-600">{reason.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mercedes Models We Service */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Mercedes Models We Service
            </h2>
            <p className="text-gray-600 text-lg">
              We service all Mercedes-Benz models, from classic to latest
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {mercedesModels.map((model, index) => (
              <div key={index} className="bg-white border border-gray-200 px-4 py-3 rounded-lg text-center hover:border-red-600 transition-all duration-300">
                <span className="text-gray-900 font-semibold">{model}</span>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-600">+ All other Mercedes-Benz models</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Ready to Service Your Mercedes?
          </h2>
          <p className="text-white/90 text-xl mb-8 max-w-2xl mx-auto">
            Book your Mercedes service today and get 15% off. Our expert technicians are ready to help!
          </p>
          
          <Button
            onClick={openWhatsApp}
            className="bg-white hover:bg-gray-100 text-red-600 px-10 py-6 rounded-lg text-xl font-bold transition-all duration-200 shadow-2xl hover:scale-105"
          >
            Book Now via WhatsApp
            <ArrowRight className="ml-2 w-6 h-6" />
          </Button>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-6 text-gray-900">
            <div className="flex items-center space-x-2">
              <span className="font-semibold">📞 056 667 1468</span>
            </div>
            <div className="hidden sm:block w-px h-6 bg-white/30"></div>
            <div>
              <span>📍 Ras Al Khor Industrial Area 1, Dubai</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
      
      {/* Call Button */}
      <CallButton />
      
      {/* WhatsApp Button */}
      <WhatsAppButton />
    </div>
  );
};
